import { type MouseEvent, useCallback, useEffect, useState } from 'react'

import { useUnit } from 'effector-react'

import ArrowIcon from '@/assets/icons/icon-arrow-left.svg'
import CloseIcon from '@/assets/icons/icon-close.svg'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { DialogProps } from '@/dialogs/types'
import { $vaultMediasMap } from '@/models/vault/medias'
import { $cloneToOriginalRegistry } from '@/models/vault/medias/clonned.ts'
import { setSelectedMedia } from '@/models/vault/medias/selection.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button.tsx'
import { classNames } from '@/utils/helpers.ts'
import { useKeydownHandler } from '@/utils/useKeydownHandler.ts'
import { useScrollHandler } from '@/utils/useScrollHandler.ts'

import { VaultMediaContent } from './VaultMediaContent.tsx'

interface Props extends DialogProps {
  /**
   * List of media between which we should be able to navigate.
   */
  mediaList: number[]
  mediaId: number
}

export const VaultMediaPreviewDialog = ({
  isOpen,
  mediaId,
  mediaList,
  onClose,
}: Props) => {
  const vaultMediasMap = useUnit($vaultMediasMap)
  const cloneToOriginalRegistry = useUnit($cloneToOriginalRegistry)
  const [currentIndex, setCurrentIndex] = useState(
    mediaList.findIndex((id) => mediaId === id),
  )
  const currentMediaId = cloneToOriginalRegistry.has(mediaList[currentIndex])
    ? cloneToOriginalRegistry.get(mediaList[currentIndex])!
    : mediaList[currentIndex]
  const isFirstSelected = currentIndex === -1 || currentIndex === 0
  const isLastSelected =
    currentIndex === -1 || currentIndex === mediaList.length - 1

  const selectNextMedia = useCallback(() => {
    if (!isLastSelected) {
      setCurrentIndex(currentIndex + 1)
    }
  }, [currentIndex, isLastSelected])

  const selectPrevMedia = useCallback(() => {
    if (!isFirstSelected) {
      setCurrentIndex(currentIndex - 1)
    }
  }, [currentIndex, isFirstSelected])

  const handleKeyPress = useCallback(
    (event: Event) => {
      if ('code' in event) {
        if (event.code === 'ArrowRight') {
          selectNextMedia()
        } else if (event.code === 'ArrowLeft') {
          selectPrevMedia()
        } else if (event.code === 'Escape') {
          onClose()
        }
      }
    },
    [onClose, selectNextMedia, selectPrevMedia],
  )

  const handlePrevButtonClick = useCallback(
    (event: MouseEvent<HTMLOrSVGElement>) => {
      event.preventDefault()
      event.stopPropagation()

      selectPrevMedia()
    },
    [selectPrevMedia],
  )
  const handleNextButtonClick = useCallback(
    (event: MouseEvent<HTMLOrSVGElement>) => {
      event.preventDefault()
      event.stopPropagation()

      selectNextMedia()
    },
    [selectNextMedia],
  )

  useKeydownHandler(handleKeyPress, isOpen && !!currentMediaId)
  useScrollHandler(onClose, isOpen && !!currentMediaId)

  useEffect(() => {
    setSelectedMedia(vaultMediasMap[currentMediaId])
  }, [currentMediaId, vaultMediasMap])

  return (
    <Dialog
      show={isOpen}
      panelClassName="lg:max-w-[100vw] h-[100vh] bg-transparent border-none sm:max-w-full"
    >
      <div
        className="absolute left-0 top-0 flex h-full w-full items-center justify-center"
        onClick={onClose}
      >
        <VaultMediaContent mediaId={currentMediaId} />
      </div>
      <IconButton
        icon={CloseIcon}
        iconClassName="h-6 w-6"
        className="absolute right-3 top-3 cursor-pointer rounded-full p-2 duration-200 text-white hover:text-black hover:bg-[#ffffff99] bg-[#000000c7]"
        onClick={onClose}
      />
      <IconButton
        icon={ArrowIcon}
        className={classNames(
          'z-1000 absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer text-[#ffffff99] duration-200 p-4 rounded-full bg-[#000000c7]',
          isFirstSelected
            ? 'cursor-not-allowed hover:text-[#ffffff99]'
            : 'hover:text-black hover:bg-[#ffffff99]',
        )}
        iconClassName="h-6 w-6"
        onClick={handlePrevButtonClick}
      />
      <IconButton
        icon={ArrowIcon}
        className={classNames(
          'z-1000 absolute right-3 top-1/2 -translate-y-1/2 rotate-180 cursor-pointer text-[#ffffff99] duration-200 p-4 rounded-full bg-[#000000c7]',
          isLastSelected
            ? 'cursor-not-allowed hover:text-[#ffffff99]'
            : 'hover:text-black hover:bg-[#ffffff99]',
        )}
        iconClassName="h-6 w-6"
        onClick={handleNextButtonClick}
      />
    </Dialog>
  )
}
